// ---------------------------------------------------------------------------
// AppLock — In-memory encryption key lifecycle
// ---------------------------------------------------------------------------
import { AppLockCryptoError } from './crypto-adapter.js';
/**
 * Manages the in-memory lifecycle of the derived encryption key.
 *
 * The key exists ONLY in a module-scoped variable:
 * - Not in `window`
 * - Not in localStorage or IndexedDB
 * - Not attached to any DOM element
 *
 * On lock, the reference is set to `null`, making the CryptoKey
 * object eligible for garbage collection.
 *
 * Limitation: JavaScript does not provide guaranteed memory zeroing.
 * The key material may linger in the V8 heap until GC. This is the
 * same limitation faced by Bitwarden, Standard Notes, and all other
 * browser-based crypto applications.
 */
export class KeyLifecycle {
    constructor() {
        this.key = null;
    }
    /** Store the derived key in memory after successful unlock. */
    hold(key) {
        this.key = key;
    }
    /**
     * Release the key from memory.
     * Called on lock, idle timeout, and tab close.
     */
    release() {
        this.key = null;
    }
    /**
     * Get the current encryption key.
     * Throws `AppLockCryptoError` with code 'locked' if no key is held.
     */
    get() {
        if (!this.key) {
            throw new AppLockCryptoError('locked', 'Cannot access encryption key — app is locked.');
        }
        return this.key;
    }
    /** Whether a key is currently held in memory. */
    get isHeld() {
        return this.key !== null;
    }
}
//# sourceMappingURL=key-lifecycle.js.map